import { useState } from 'react';
import {
  SpaceBetween,
  Button,
  Box,
  Modal,
  Textarea,
  Alert,
} from '@cloudscape-design/components';
import { ConfigurationObject, updateConfig, softDeleteConfig } from '../services/configs-api';
import { useNotifications } from '@features/notifications';
import { usePermissions } from '@features/auth';

interface BulkActionsToolbarProps {
  selectedItems: ConfigurationObject[];
  onClearSelection: () => void;
  onBulkComplete: () => void;
}

type BulkAction = 'enableEmergency' | 'disableEmergency' | 'export' | 'delete';

/**
 * Bulk actions toolbar that appears when configs are selected via checkboxes.
 * BR-15 Part B: Enable/Disable Emergency Mode, Export, Delete.
 */
export function BulkActionsToolbar({ selectedItems, onClearSelection, onBulkComplete }: BulkActionsToolbarProps) {
  const [confirmAction, setConfirmAction] = useState<BulkAction | null>(null);
  const [emergencyMessage, setEmergencyMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { addNotification } = useNotifications();
  const { canWrite } = usePermissions();

  if (selectedItems.length === 0) return null;

  const queueAndDnisCount = selectedItems.filter(
    (item) => item.configType === 'queues' || item.configType === 'dnis'
  ).length;
  const skippedCount = selectedItems.length - queueAndDnisCount;

  const handleConfirm = async () => {
    setIsProcessing(true);

    try {
      switch (confirmAction) {
        case 'enableEmergency': {
          const applicable = selectedItems.filter(
            (item) => item.configType === 'queues' || item.configType === 'dnis'
          );
          let successCount = 0;
          for (const item of applicable) {
            try {
              await updateConfig(item.configId, {
                configName: item.configName,
                configType: item.configType,
                payload: { ...item.payload, emergencyEnabled: true, ...(emergencyMessage ? { emergencyMessage } : {}) },
                version: item.version,
              });
              successCount++;
            } catch {
              // Continue with remaining items
            }
          }
          const resultMsg = `Emergency Mode enabled for ${successCount} configuration${successCount !== 1 ? 's' : ''}.${skippedCount > 0 ? ` ${skippedCount} configuration${skippedCount !== 1 ? 's were' : ' was'} skipped (not a Queue or DNIS type).` : ''}`;
          addNotification('success', resultMsg);
          onBulkComplete();
          break;
        }
        case 'disableEmergency': {
          const applicable = selectedItems.filter(
            (item) => item.configType === 'queues' || item.configType === 'dnis'
          );
          let successCount = 0;
          for (const item of applicable) {
            try {
              await updateConfig(item.configId, {
                configName: item.configName,
                configType: item.configType,
                payload: { ...item.payload, emergencyEnabled: false },
                version: item.version,
              });
              successCount++;
            } catch {
              // Continue with remaining items
            }
          }
          const resultMsg = `Emergency Mode disabled for ${successCount} configuration${successCount !== 1 ? 's' : ''}.${skippedCount > 0 ? ` ${skippedCount} configuration${skippedCount !== 1 ? 's were' : ' was'} skipped (not a Queue or DNIS type).` : ''}`;
          addNotification('success', resultMsg);
          onBulkComplete();
          break;
        }
        case 'export': {
          const blob = new Blob([JSON.stringify(selectedItems, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `configurations-export-${new Date().toISOString().split('T')[0]}.json`;
          a.click();
          URL.revokeObjectURL(url);
          addNotification('success', `Exported ${selectedItems.length} configuration${selectedItems.length !== 1 ? 's' : ''}`);
          break;
        }
        case 'delete': {
          let successCount = 0;
          for (const item of selectedItems) {
            try {
              await softDeleteConfig(item.configId, `Bulk delete operation`);
              successCount++;
            } catch {
              // Continue with remaining items
            }
          }
          addNotification('success', `${successCount} configuration${successCount !== 1 ? 's' : ''} scheduled for deletion (24-hour grace period).`);
          onBulkComplete();
          break;
        }
      }
    } catch (e) {
      const { translateError } = await import('@features/notifications/error-messages');
      const translated = translateError(e);
      addNotification('error', translated.message, translated.technicalDetails);
    } finally {
      setIsProcessing(false);
      setConfirmAction(null);
      setEmergencyMessage('');
      onClearSelection();
    }
  };

  const getConfirmTitle = () => {
    switch (confirmAction) {
      case 'enableEmergency': return 'Enable Emergency Mode';
      case 'disableEmergency': return 'Disable Emergency Mode';
      case 'export': return 'Export Configurations';
      case 'delete': return 'Delete Configurations';
      default: return '';
    }
  };

  return (
    <>
      <Box
        padding={{ vertical: 's', horizontal: 'm' }}
        variant="div"
      >
        <SpaceBetween direction="horizontal" size="m">
          <Box variant="strong">{selectedItems.length} selected</Box>
          {canWrite && (
            <Button variant="normal" onClick={() => setConfirmAction('enableEmergency')}>
              Enable Emergency Mode
            </Button>
          )}
          {canWrite && (
            <Button variant="normal" onClick={() => setConfirmAction('disableEmergency')}>
              Disable Emergency Mode
            </Button>
          )}
          <Button variant="normal" onClick={() => setConfirmAction('export')}>
            Export selected
          </Button>
          {canWrite && (
            <Button variant="normal" onClick={() => setConfirmAction('delete')}>
              Delete selected
            </Button>
          )}
          <Button variant="link" onClick={onClearSelection}>
            Clear selection
          </Button>
        </SpaceBetween>
      </Box>

      {/* Confirmation modal */}
      <Modal
        visible={!!confirmAction}
        onDismiss={() => setConfirmAction(null)}
        header={getConfirmTitle()}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button variant="primary" loading={isProcessing} onClick={handleConfirm}>
              Confirm
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning">
            You are about to {getConfirmTitle().toLowerCase()} {selectedItems.length} configuration{selectedItems.length !== 1 ? 's' : ''}:
          </Alert>
          <Box variant="small">
            {selectedItems.map((item) => (
              <div key={item.configId}>• {item.configName} ({item.configType})</div>
            ))}
          </Box>

          {confirmAction === 'enableEmergency' && (
            <>
              {skippedCount > 0 && (
                <Alert type="info">
                  {skippedCount} configuration{skippedCount !== 1 ? 's' : ''} will be skipped (not Queue or DNIS type).
                </Alert>
              )}
              <Textarea
                value={emergencyMessage}
                onChange={({ detail }) => setEmergencyMessage(detail.value)}
                placeholder="Enter emergency message to apply (or leave blank to keep existing messages)..."
                rows={3}
              />
            </>
          )}

          {confirmAction === 'enableEmergency' || confirmAction === 'disableEmergency' ? (
            <Box variant="small" color="text-status-warning">
              This will affect live call routing.
            </Box>
          ) : null}
        </SpaceBetween>
      </Modal>
    </>
  );
}
